<script setup lang="ts">
import dayjs from 'dayjs';
import { BLOCK_TYPES, normalizeBlockType, type AvailabilityEntry } from '@/types/availability';
import { isAllDayAvailabilityEntry } from '@/util/availabilityDisplay';

withDefaults(defineProps<{ entries: AvailabilityEntry[]; showDates?: boolean; kind?: 'available' | 'blocked' }>(), { showDates: false, kind: 'blocked' });

function scopeLabel(entry: AvailabilityEntry) {
	const type = normalizeBlockType(entry.blockType);
	if (type === BLOCK_TYPES.BLOCK_CLIENT || type === BLOCK_TYPES.AVAILABLE_CLIENT) return entry.client?.name || 'Selected client';
	if (type === BLOCK_TYPES.BLOCK_GROUP || type === BLOCK_TYPES.AVAILABLE_GROUP) return entry.group?.shortName || entry.group?.name || 'Selected group';
	return 'All groups';
}

function timeLabel(entry: AvailabilityEntry) {
	if (isAllDayAvailabilityEntry(entry)) return 'All day';
	const start = entry.startTime ? dayjs(`2000-01-01T${entry.startTime}`).format('h:mm A') : 'Start of day';
	const end = entry.endTime ? dayjs(`2000-01-01T${entry.endTime}`).format('h:mm A') : 'End of day';
	return `${start} – ${end}`;
}

function dateLabel(entry: AvailabilityEntry) {
	const start = dayjs(entry.startDate).format('MMM D, YYYY');
	return entry.startDate === entry.endDate ? start : `${start} – ${dayjs(entry.endDate).format('MMM D, YYYY')}`;
}
</script>

<template>
	<div v-if="entries.length" class="text-sm" :class="kind === 'available' ? 'availability-offer-details' : 'availability-block-details'">
		<div class="font-medium mb-1" :class="kind === 'available' ? 'text-green-700 dark:text-green-200' : 'text-red-700 dark:text-red-200'">{{ kind === 'available' ? 'Available times and groups' : 'Blocked times and groups' }}</div>
		<ul class="m-0 list-none p-0 space-y-2">
			<li v-for="(entry, index) in entries" :key="entry.id ?? index" class="text-color-secondary">
				<div v-if="showDates" class="text-xs">{{ dateLabel(entry) }}</div>
				<div class="break-words">
					<span class="font-medium text-color">{{ scopeLabel(entry) }}</span> · {{ timeLabel(entry) }}
				</div>
				<div v-if="entry.notes" class="text-xs break-words">{{ entry.notes }}</div>
			</li>
		</ul>
	</div>
</template>
